import { createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import { isServer } from 'solid-js/web';
import { createAsync, useAction, useSubmission } from '@solidjs/router';
import { listenForCookieYesConsentUpdates } from '@troon/analytics';
import { cachedGet, clientAction } from '@troon/api-client';
import type { CookieYesConsentState } from '@troon/analytics';
import type { ApiBody, ApiResponse } from '@troon/api-client';
import type { AccessorWithLatest } from '@solidjs/router';
import type { UserData } from './user';

type ConsentUpdate = ApiBody<'post', '/v0/users/account/data-sharing-consent'>;
type CurrentConsent = ApiResponse<'get', '/v0/users/account/data-sharing-consent'>['current'];

const AUTO_OPT_OUT_PRIVACY_LAW_KEYS = [
	// General Data Protection Regulation
	'gdpr',

	// Personal Information Protection and Electronic Documents Act
	'pipeda',
	'canada',

	// Quebec Law 25
	'law25',
	'quebec',
];

export function DataSharingConsentSync(props: { user: AccessorWithLatest<UserData | null> }) {
	if (isServer) {
		return null;
	}

	const [cookieConsent, setCookieConsent] = createSignal<CookieYesConsentState>();
	const currentConsent = createAsync(() => {
		const user = props.user();
		const currentCookie = getDataSharingConsentBody(cookieConsent());
		// CookieYes is the source of the desired state; without a sync candidate there is no backend work to do.
		if (!user || !currentCookie) {
			return Promise.resolve(undefined);
		}

		return getCurrentDataSharingConsent({}).catch(() => undefined);
	});
	const updateConsent = useAction(updateDataSharingConsent);
	const updateSubmission = useSubmission(updateDataSharingConsent);

	let lastUserId: string | undefined;
	let lastSyncKey: string | undefined;

	onMount(() => {
		const cleanup = listenForCookieYesConsentUpdates({
			onRead: setCookieConsent,
		});
		onCleanup(cleanup);
	});

	createEffect(() => {
		const user = props.user();
		if (lastUserId !== user?.id) {
			lastUserId = user?.id;
			lastSyncKey = undefined;
		}

		const currentServer = currentConsent();
		const currentCookie = getDataSharingConsentBody(cookieConsent());

		if (!user) {
			return;
		}

		if (currentServer === undefined) {
			return;
		}

		if (!currentCookie) {
			return;
		}

		if (updateSubmission.pending) {
			return;
		}

		if (currentMatchesUpdate(currentServer.current, currentCookie)) {
			return;
		}

		const syncKey = `${user.id}:${currentCookie.privacyLaw ?? ''}:${currentCookie.optedOut}:${currentCookie.type}`;
		if (lastSyncKey === syncKey) {
			return;
		}
		lastSyncKey = syncKey;

		void updateConsent(createConsentFormData(currentCookie)).catch(() => {
			// Clear the session guard so a later CookieYes/backend state change can retry after a transient failure.
			if (lastUserId === user.id) {
				lastSyncKey = undefined;
			}
		});
	});

	return null;
}

export function getDataSharingConsentBody(cookieConsent?: CookieYesConsentState): ConsentUpdate | undefined {
	if (!cookieConsent) {
		return undefined;
	}

	// Some regional privacy laws default users to opted out until CookieYes reports an explicit user action.
	if (isAutomaticOptOutPrivacyLaw(cookieConsent.activeLaw) && cookieConsent.isUserActionCompleted === false) {
		return {
			privacyLaw: cookieConsent.activeLaw,
			optedOut: true,
			type: 'AUTO',
		};
	}

	// CookieYes may expose default category state before interaction; only completed actions are SELECTED.
	if (cookieConsent.isUserActionCompleted !== true) {
		return undefined;
	}

	const optedOut = getOptedOutFromCategories(cookieConsent.categories);
	if (optedOut === undefined) {
		return undefined;
	}

	return {
		privacyLaw: cookieConsent.activeLaw ?? null,
		optedOut,
		type: 'SELECTED',
	};
}

export function isAutomaticOptOutPrivacyLaw(activeLaw?: string) {
	return matchesNormalizedKey(activeLaw, AUTO_OPT_OUT_PRIVACY_LAW_KEYS);
}

function getOptedOutFromCategories(categories: CookieYesConsentState['categories']) {
	return typeof categories.advertisement === 'boolean' ? !categories.advertisement : undefined;
}

function currentMatchesUpdate(current: CurrentConsent | null, update: ConsentUpdate) {
	return (
		(current?.privacyLaw ?? null) === (update.privacyLaw ?? null) &&
		current?.optedOut === update.optedOut &&
		current?.type === update.type
	);
}

function matchesNormalizedKey(value: string | undefined, keys: Array<string>) {
	const normalized = value?.toLowerCase().replace(/[^a-z0-9]/g, '') ?? '';
	return !!normalized && keys.some((key) => normalized.includes(key));
}

const getCurrentDataSharingConsent = cachedGet('/v0/users/account/data-sharing-consent', undefined, {
	// This provider runs after auth resolution; unauthorized reads should skip sync, not redirect the user.
	redirect401: false,
});

const updateDataSharingConsent = clientAction(
	'post',
	'/v0/users/account/data-sharing-consent',
	undefined,
	{
		revalidate: ['/v0/users/account/data-sharing-consent'],
		// clientAction expands FormData into body/query/path; keep this POST body to the consent API contract.
		transformer: getConsentUpdateFromFormData,
	},
	'update-data-sharing-consent',
);

function createConsentFormData(update: ConsentUpdate) {
	const formData = new FormData();
	if (update.privacyLaw) {
		formData.set('privacyLaw', update.privacyLaw);
	}
	formData.set('optedOut', update.optedOut ? 'true' : 'false');
	formData.set('type', update.type);
	return formData;
}

function getConsentUpdateFromFormData(formData: FormData): ConsentUpdate {
	return {
		privacyLaw: `${formData.get('privacyLaw') ?? ''}` || null,
		optedOut: formData.get('optedOut') === 'true',
		type: formData.get('type') as ConsentUpdate['type'],
	};
}
